import { v4 as uuidv4 } from 'uuid'

import { MEDIA_DURATION, MEDIA_FORMAT, MEDIA_HEIGHT, MEDIA_RATIO, MEDIA_SIZE, MEDIA_WIDTH } from './fields'

interface FileDetails {
  url: string
  size: number
  fileFormat: string
}

interface ImageValues {
  [MEDIA_SIZE]: number
  [MEDIA_WIDTH]: number
  [MEDIA_HEIGHT]: number
  [MEDIA_FORMAT]: string
  [MEDIA_RATIO]: number
}

interface VideoValues extends ImageValues {
  [MEDIA_DURATION]: number
}

export async function getImageValues(fileDetails: FileDetails): Promise<ImageValues> {
  const { url, size, fileFormat } = fileDetails

  const image = new Image()
  image.src = url

  return await new Promise(resolve => {
    image.onload = function () {
      const newImageValues = {
        [MEDIA_SIZE]: size,
        [MEDIA_WIDTH]: image.width,
        [MEDIA_HEIGHT]: image.height,
        [MEDIA_FORMAT]: fileFormat,
        [MEDIA_RATIO]: image.width / image.height
      }
      resolve(newImageValues)
    }
  })
}

export async function getVideoValues(fileDetails: FileDetails): Promise<VideoValues> {
  const { url, size, fileFormat } = fileDetails

  const video = document.createElement('video')
  video.src = url

  return await new Promise(resolve => {
    video.preload = 'metadata'
    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src)

      const newVideoValues = {
        [MEDIA_SIZE]: size,
        [MEDIA_WIDTH]: video.videoWidth,
        [MEDIA_HEIGHT]: video.videoHeight,
        [MEDIA_FORMAT]: fileFormat,
        [MEDIA_RATIO]: video.videoWidth / video.videoHeight,
        [MEDIA_DURATION]: video.duration
      }
      resolve(newVideoValues)
    }
  })
}

export function getMultipleMediaUploadsValues<TMedia extends { id: string } & Record<string, any>, TRaw = unknown>({
  files,
  newMediaItemsValues,
  currentMediasValues,
  index,
  initialFileValues,
  formatMediaFile
}: {
  files: { length: number }
  newMediaItemsValues: TRaw[]
  currentMediasValues: TMedia[]
  index: number
  initialFileValues: Partial<TMedia>
  formatMediaFile: (item: TRaw) => Partial<TMedia>
}): TMedia[] {
  // this component is shown by initial media item values which added to formik values
  // once new file or files are uploaded this values should be updated with the first file in "files" array
  const currentMediaUpdatedValues = formatMediaFile(newMediaItemsValues[0])
  const newMedia = { id: uuidv4(), ...initialFileValues } as TMedia

  // update current media
  const updatedMediasValues: TMedia[] = currentMediasValues.map((media, i) => {
    if (i === index) {
      return { ...media, ...currentMediaUpdatedValues } as TMedia
    }
    return media
  })

  if (files.length === 1) {
    // single media file was uploaded
    // update current media + add initial media item
    updatedMediasValues.push(newMedia)
  } else {
    // multiple media files were uploaded
    // add new media items with values excluding first file as it was updated within the current media
    const newAdsMediaItems: TMedia[] = newMediaItemsValues.slice(1).map(item => {
      const newMediaItemValues = formatMediaFile(item)
      return {
        // this is a temporary id for the media file uploading to avoid preview disappearing on other media files removal
        id: uuidv4(),
        ...initialFileValues,
        ...newMediaItemValues
      } as TMedia
    })

    // update current media + add new media items with values + add initial media item
    updatedMediasValues.push(...newAdsMediaItems, newMedia)
  }

  return updatedMediasValues
}
